import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ApiError, errorMessage } from '@/domains/api';
import { idTypeLabel } from '@/domains/auth';
import { surveyApi } from '../api';
import type { IMyIdentifier, IMyParticipant, SelfConfirmation } from '../api/contracts';

/**
 * survey-api's one question to the owner of a number — is this one yours? — over
 * `GET /my/identifiers` and `POST /my/identifier-confirmations` (SR-213 block 3, roadmap D2).
 *
 * **A card on the catalogue, not a screen in front of it.** survey-api ruled the confirmation
 * no gate: an unanswered one blocks nothing, and neither does a denial, because the
 * participant holds the responses the rankings were built from. An interstitial would be this
 * app inventing the gate the server declined to have.
 *
 * **One card per row whose `selfConfirmation` is null, and no other filter** — the server's
 * rule for when to ask. That includes the registrant who said "yes, that's me" at
 * `confirm_identity` a minute earlier: registration leaves the column null (ask 1 of
 * survey-api in the registration-flow plan), and the owner ruled that the card shows as the
 * server answers. Filtering on `verifiedVia` would be a rule of the server's made here, and
 * would miss the `error` path, where it is null.
 *
 * **Nothing on it claims verification.** The answer writes `self_confirmation` and never
 * `verified_at` (survey-api's R1: an owner's word is not a corroboration), so the card says
 * neither "verified" nor what verification the row already carries — beside the answer, that
 * would read as the answer's effect.
 */

interface IIdentifierConfirmationsProps {
  /** `null` while `GET /my/identifiers` has not landed, or failed — both render nothing, the
   *  question being no gate. */
  identifiers: Nullable<IMyIdentifier[]>;
  /** For the name a number is held for. `null` leaves the card saying "your record". */
  participants: Nullable<IMyParticipant[]>;
  /** Handed the row as survey-api answered it, for the page to replace the one it read. */
  onAnswered: (row: IMyIdentifier) => void;
}

/** A row answered during this visit, and whether it was this visit that answered it. */
interface IAnswered {
  row: IMyIdentifier;
  /** What was pressed differs from what came back: another tab, or another device, answered
   *  first, and survey-api kept that answer. */
  earlier: boolean;
}

export function IdentifierConfirmations({
  identifiers,
  participants,
  onAnswered
}: IIdentifierConfirmationsProps) {
  // Session-only, and nothing on the wire needs it to be otherwise: the row itself carries
  // the answer from here on, so a reload simply stops asking. This is only what the visit
  // that answered says about it.
  const [answered, setAnswered] = React.useState<IAnswered[]>([]);

  const record = (row: IMyIdentifier, pressed: SelfConfirmation) => {
    setAnswered((current) => [
      ...current.filter((entry) => entry.row.identifierId !== row.identifierId),
      { row, earlier: row.selfConfirmation !== pressed }
    ]);
    onAnswered(row);
  };

  const unasked = (identifiers ?? []).filter((row) => row.selfConfirmation === null);
  const nameOf = (row: IMyIdentifier) =>
    participants?.find((participant) => participant.participantId === row.participantId)
      ?.displayName ?? null;

  return (
    <>
      {answered.map((entry) => (
        <AnswerNote key={entry.row.identifierId} entry={entry} />
      ))}
      {unasked.map((row) => (
        <IdentifierCard
          key={row.identifierId}
          row={row}
          name={nameOf(row)}
          onAnswered={record}
        />
      ))}
    </>
  );
}

function IdentifierCard({
  row,
  name,
  onAnswered
}: {
  row: IMyIdentifier;
  name: Nullable<string>;
  onAnswered: (row: IMyIdentifier, pressed: SelfConfirmation) => void;
}) {
  const [pending, setPending] = React.useState<Nullable<SelfConfirmation>>(null);
  const [error, setError] = React.useState<Nullable<string>>(null);

  const answer = async (selfConfirmation: SelfConfirmation) => {
    setPending(selfConfirmation);
    setError(null);
    try {
      const kept = await surveyApi.confirmIdentifier(row.identifierId, selfConfirmation);
      onAnswered(kept, selfConfirmation);
    } catch (answerError) {
      setError(answerFailureMessage(answerError));
      setPending(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Is this number yours?</CardTitle>
        <CardDescription>
          {name ? `We hold it for ${name}.` : 'We hold it on your record.'} Your surveys stay
          open whichever you answer.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <dl className="grid gap-1 rounded-md border border-border bg-[var(--color-slight-blue)]/40 p-4">
          <dt className="text-sm text-muted-foreground">{idTypeLabel(row.idType)}</dt>
          <dd className="font-mono text-lg text-[var(--color-royal-blue)]">{row.value}</dd>
        </dl>

        <div className="flex flex-wrap gap-3">
          <Button disabled={pending !== null} onClick={() => void answer('confirmed')}>
            {pending === 'confirmed' ? 'Saving…' : 'Yes, that’s mine'}
          </Button>
          <Button
            variant="outline"
            disabled={pending !== null}
            onClick={() => void answer('denied')}
          >
            {pending === 'denied' ? 'Saving…' : 'That is not mine'}
          </Button>
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * What the visit says once a number is answered, from the row survey-api sent back rather than
 * from the button: a second answer is answered with the first, so a denial pressed here can
 * come back confirmed, and the screen must not report the one that was not kept.
 *
 * A denial says that it clears nothing, because it does not — not the number, not the
 * responses, not the survey — and a respondent who has just said "that is not mine" will
 * otherwise assume something was taken away.
 */
function AnswerNote({ entry }: { entry: IAnswered }) {
  const { row, earlier } = entry;
  const number = `${idTypeLabel(row.idType)} ${row.value}`;
  const denied = row.selfConfirmation === 'denied';

  let said: string;
  if (earlier) {
    said =
      `${number} had already been answered, and the first answer is the one kept: ` +
      `it is ${denied ? 'not yours' : 'yours'}.`;
  } else if (denied) {
    said = `Noted — you told us ${number} is not yours.`;
  } else {
    said = `Thank you — you told us ${number} is yours.`;
  }

  return (
    <Alert>
      <AlertDescription>
        {said}
        {denied && ' Nothing has been removed or changed, and your surveys stay open.'}
      </AlertDescription>
    </Alert>
  );
}

/** A 404 is one answer for a row that is gone and a row that is not this account's, and
 *  pressing again will not change it — so it says to reload rather than to retry. */
function answerFailureMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return 'That number is no longer on your record. Reload the page to see what is.';
  }
  return errorMessage(error, 'Could not record your answer. Try again.');
}
